import { createFileRoute } from '@tanstack/react-router'
import { Suspense, lazy, useEffect, useMemo, useRef, useDeferredValue } from 'react'
import FullPageLoader from '../components/ui/FullPageLoader'
import { CONFIG } from '../config/constants'
import { useAnalysisHistory } from '../hooks/useAnalysisHistory'
import { useDashboardData } from '../hooks/useDashboardData'
import { usePageTitle } from '../hooks/usePageTitle'
import { useFastMount } from '../hooks/useFastMount'
import { useSettingsStore } from '../stores/settingsStore'
import { LoadingState } from '../components/ui/LoadingState'

import { ShiftChart } from '../components/charts/ShiftChart'
import { CrossoverHistoryTable } from '../components/tables/CrossoverHistoryTable'
import { CurrentCrossoverTable } from '../components/tables/CurrentCrossoverTable'

export const Route = createFileRoute('/_auth/crossover')({
    component: CrossoverRoute,
})

function CrossoverRoute() {
    usePageTitle('Crossover Analysis')
    // FREEZE FIX v3: Granular selectors prevent re-renders from unrelated store changes
    const symbol = useSettingsStore(s => s.symbol)
    const expiry = useSettingsStore(s => s.expiry)
    const mode = useSettingsStore(s => s.mode)
    const range = useSettingsStore(s => s.range)
    const baseTime = useSettingsStore(s => s.baseTime)
    const rowsPerPage = useSettingsStore(s => s.rowsPerPage)
    const setRowsPerPage = useSettingsStore(s => s.setRowsPerPage)
    const isLive = useSettingsStore(s => s.isLive)
    const setExpiry = useSettingsStore(s => s.setExpiry)

    // FREEZE FIX: Defer the parameters passed to heavy sub-components
    const deferredSymbol = useDeferredValue(symbol)
    const deferredExpiry = useDeferredValue(expiry)
    const deferredMode = useDeferredValue(mode)
    const deferredRange = useDeferredValue(range)

    // 1. Live Data (for syncing expiry/prices)
    const { marketData, analysis, isLoading: isMarketLoading, effectiveExpiry } = useDashboardData()

    // 🚀 PERF: Defer heavy data so live ticks don't block user interactions
    const deferredMarketData = useDeferredValue(marketData)
    const deferredAnalysis = useDeferredValue(analysis)

    // FREEZE FIX v3: Ref-based guard prevents circular setExpiry loop
    const expiryRef = useRef(expiry)
    expiryRef.current = expiry
    useEffect(() => {
        if (effectiveExpiry && effectiveExpiry !== expiryRef.current) {
            setExpiry(effectiveExpiry)
        }
    }, [effectiveExpiry, setExpiry])

    // 2. History Data
    const historyData = useAnalysisHistory({
        symbol: deferredSymbol,
        expiry: deferredExpiry || effectiveExpiry || '',
        range: deferredRange,
        mode: deferredMode,
        baseTimePattern: baseTime,
        viewType: 'default',
    })

    // 3. Construct Live Data Point (for Charts & Table)
    const liveDataPoint = useMemo(() => {
        if (!isLive || !analysis || !marketData) return null

        // Helper to find option
        const findOption = (strike: number) => marketData.options?.find((o: any) => o.strike === strike)

        const resOpt = findOption(analysis.resistanceStrike)
        const supOpt = findOption(analysis.supportStrike)

        // Determine values based on mode (OI or Volume)
        const isVol = mode === 'VOLUME' // Simple check, or import AnalysisMode enum if available

        return {
            timestamp: new Date(marketData.timestamp || Date.now()).toISOString(),
            rank: 1,

            // Prices
            ceLtp: resOpt?.callPrice || 0,
            peLtp: supOpt?.putPrice || 0,

            // Analysis Values
            resistanceValue: isVol ? resOpt?.callVolume || 0 : resOpt?.callOpenInterest || 0,
            supportValue: isVol ? supOpt?.putVolume || 0 : supOpt?.putOpenInterest || 0,

            // Strikes
            resistanceStrike: analysis.resistanceStrike,
            resistanceStrike2: analysis.resistanceStrike2,
            supportStrike: analysis.supportStrike,
            supportStrike2: analysis.supportStrike2,

            // Variance
            ceCount: analysis.variance?.ceCount ?? 0,
            peCount: analysis.variance?.peCount ?? 0,
            finalCount: analysis.variance?.totalCount ?? 0,

            maxDiff: analysis.variance?.maxDiff ?? 0,
            minDiff: analysis.variance?.minDiff ?? 0,
            finalVariance: analysis.variance?.finalVariance ?? 0,

            // Critical Metrics
            shift: analysis.shift,
            atmStrike: analysis.atmStrike,
        }
    }, [isLive, analysis, marketData, mode])

    // 4. Merge Data (History + Live)
    const mergedData = useMemo(() => {
        const hist = historyData.data || []
        let rawData = []
        if (liveDataPoint) {
            rawData = [...hist, liveDataPoint]
        } else {
            rawData = [...hist]
        }

        // Sort by timestamp ASC for correct history time flow
        const sortedAsc = rawData.sort((a, b) => {
            if (!a || !b) return 0
            return new Date(a.timestamp || 0).getTime() - new Date(b.timestamp || 0).getTime()
        })

        return sortedAsc.reverse()
    }, [historyData.data, liveDataPoint])



    const isMounted = useFastMount()

    if (!isMounted || (isMarketLoading && !marketData && (!historyData.data || historyData.data.length === 0))) {
        return (
            <div className="space-y-6 pb-20 p-4">
                <div className="min-h-[450px] w-full rounded-2xl bg-slate-100/50 dark:bg-slate-800/20 animate-pulse border border-slate-200/50 dark:border-slate-700/50" />
                <div className="min-h-[380px] w-full rounded-2xl bg-slate-100/50 dark:bg-slate-800/20 animate-pulse border border-slate-200/50 dark:border-slate-700/50" />
            </div>
        )
    }

    return (
        <div className="space-y-6 pb-20 animate-in fade-in duration-500">
            {/* Current Analysis Table */}
            <CurrentCrossoverTable
                marketData={deferredMarketData}
                analysis={deferredAnalysis}
                isLive={isLive}
                mode={deferredMode}
                historyData={historyData.data || []}
            />

            <div className="grid grid-cols-1 gap-6">
                <ShiftChart data={mergedData} symbol={deferredSymbol} isLoading={historyData.isLoading} />
            </div>

            {/* Analysis Table */}
            <CrossoverHistoryTable
                data={mergedData}
                symbol={deferredSymbol}
                mode={deferredMode}
                // ts-expect-error - Prop naming mismatch handling if any, but standardizing on rowsPerPage   isLoading={historyData.isLoading}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={setRowsPerPage}
            />
        </div>
    )
}
